import { useMemo } from "react";
import type { GrepPaperGroup, ParsedGrep } from "../types";
import { docAnchorId } from "./SearchResultsView";
import { DOT, META } from "./styles";

/** Bold every substring the grep regex matched. The `ignoreCase` flag mirrors
 * what the backend actually did (corpus/result-set grep match case-insensitively
 * even without -i). Falls back to plain text if the pattern isn't a valid JS
 * regex. */
export const HighlightedMatch = ({
  text,
  regex,
}: {
  text: string;
  regex: RegExp | null;
}) => {
  if (!regex) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let last = 0;
  regex.lastIndex = 0;
  for (let m = regex.exec(text); m; m = regex.exec(text)) {
    if (m[0].length === 0) break; // zero-width match — bail, avoid an infinite loop
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <strong key={m.index} className="font-bold text-zinc-900">
        {m[0]}
      </strong>,
    );
    last = m.index + m[0].length;
  }
  if (parts.length === 0) return <>{text}</>;
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

// Papers (and pasted patterns) mix the micro sign (µ, U+00B5) and Greek mu
// (μ, U+03BC) freely, and the backend folds them when matching. If the client
// highlighter doesn't, a paper the server matched on "5 µM" never bolds when the
// user typed "μM" — the match silently drops out. Fold every bare µ/μ into a
// class that accepts both. Skip chars already inside a bracket class to avoid
// nesting `[[µμ]]`.
const foldMicroSign = (src: string): string => {
  let depth = 0;
  let out = "";
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === "\\") {
      out += ch + (src[i + 1] ?? "");
      i++;
      continue;
    }
    if (ch === "[") depth++;
    else if (ch === "]" && depth > 0) depth--;
    out += depth === 0 && (ch === "\u00b5" || ch === "\u03bc") ? "[\u00b5\u03bc]" : ch;
  }
  return out;
};

// Unbounded greedy gaps (`.*`, `[^\n]+`, …) let an early token splice to an
// unrelated one far down the paragraph, so the bolded run balloons across a
// whole blob (or, once windowed, lands off-screen). For *highlighting only*
// bound them and make them lazy so the bolded span stays local; the server
// query is untouched, so match counts don't change.
const GAP_CAP = 160;
const boundGreedyGaps = (src: string): string =>
  src
    .replace(/\[\^\\?n\]\*(?!\?)/g, `[^\\n]{0,${GAP_CAP}}?`)
    .replace(/\[\^\\?n\]\+(?!\?)/g, `[^\\n]{1,${GAP_CAP}}?`)
    .replace(/(^|[^\\])\.\*(?!\?)/g, `$1.{0,${GAP_CAP}}?`)
    .replace(/(^|[^\\])\.\+(?!\?)/g, `$1.{1,${GAP_CAP}}?`);

export const compileGrepRegex = (
  pattern?: string,
  ignoreCase = false,
  fixedStrings = false,
  wholeWords = false,
): RegExp | null => {
  if (!pattern) return null;
  try {
    const source = foldMicroSign(
      fixedStrings
        ? pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        : boundGreedyGaps(pattern),
    );
    return new RegExp(
      wholeWords ? `\\b(?:${source})\\b` : source,
      ignoreCase ? "gi" : "g",
    );
  } catch {
    return null;
  }
};

/**
 * Corpus grep prefixes each preview with its (possibly truncated) VFS path and
 * line marker — "/papers/…/Related_Work.lines:L17: …" or "…nes:L24: …". Drop it
 * so the preview reads as prose; the section/line already show elsewhere.
 */
const stripPathPrefix = (content: string): string => {
  const withoutMarker = content.replace(/^…?\s*\S*?:L\d+:\s*/, "");
  return withoutMarker !== content
    ? withoutMarker
    : content.replace(/^…+\s*/, "");
};

// Blocks are whole paragraphs, so the matched term often sits well past the
// 2-line clamp and never renders. Window the preview around the first hit
// (grep-style context) so the bolded match is always visible.
const CTX_BEFORE = 48;
const CTX_AFTER = 180;
const snippetAroundMatch = (text: string, regex: RegExp | null): string => {
  if (!regex) return text;
  regex.lastIndex = 0;
  const m = regex.exec(text);
  if (!m || m[0].length === 0) return text; // no visible hit — show the head
  if (m.index <= CTX_BEFORE && text.length <= CTX_BEFORE + m[0].length + CTX_AFTER) {
    return text; // already short and match near the start
  }
  const start = Math.max(0, m.index - CTX_BEFORE);
  const end = Math.min(text.length, m.index + m[0].length + CTX_AFTER);
  return `${start > 0 ? "…" : ""}${text.slice(start, end)}${end < text.length ? "…" : ""}`;
};

/** Grouped corpus-grep matches; clicking a paper opens it with the matching
 * blocks highlighted. */
export const GrepResultsView = ({
  grep,
  pattern,
  ignoreCase,
  fixedStrings,
  wholeWords,
  onOpenPaper,
}: {
  grep: ParsedGrep;
  pattern?: string;
  ignoreCase?: boolean;
  fixedStrings?: boolean;
  wholeWords?: boolean;
  onOpenPaper: (paper: GrepPaperGroup) => void;
}) => {
  const regex = useMemo(
    () => compileGrepRegex(pattern, ignoreCase, fixedStrings, wholeWords),
    [fixedStrings, ignoreCase, pattern, wholeWords],
  );
  if (grep.papers.length === 0) {
    return (
      <p className="py-12 text-center text-base italic text-zinc-500">
        No matches across the corpus. Grep is exact — check spelling, or try a
        looser regex.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-3 border-b border-zinc-200 pb-2 text-sm text-zinc-400">
        <span className="text-zinc-700">
          {grep.totalMatches} paragraph{grep.totalMatches === 1 ? "" : "s"} in{" "}
          {grep.paperCount} paper{grep.paperCount === 1 ? "" : "s"}
        </span>
        {grep.resultsId && (
          <span className="font-mono text-2xs">{grep.resultsId}</span>
        )}
        <span className="ml-auto hidden italic sm:block">Click a paper to see matches in context</span>
      </div>
      <ul>
        {grep.papers.map((group) => (
          <li
            key={group.documentId}
            id={docAnchorId(group.documentId)}
            className="scroll-mt-24"
          >
            <button
              type="button"
              onClick={() => onOpenPaper(group)}
              className="group -mx-3 w-[calc(100%+1.5rem)] border-b border-zinc-200/70 px-3 py-4 text-left transition-colors first:pt-0 hover:bg-zinc-900/[0.03]"
            >
              <div className={`${META} flex items-baseline gap-x-2`}>
                <span className="font-mono text-xs text-zinc-900">{group.documentId}</span>
                <span aria-hidden className={DOT}>·</span>
                <span>
                  {group.matchCount} match{group.matchCount === 1 ? "" : "es"}
                </span>
                <span className="ml-auto text-zinc-400 transition-colors group-hover:text-zinc-700">Read →</span>
              </div>
              <ul className="mt-2 space-y-1.5">
                {group.matches.map((match, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm leading-6 text-zinc-600"
                  >
                    {match.section && (
                      <span className="shrink-0 text-2xs uppercase tracking-[0.14em] text-zinc-400">
                        {match.section}
                      </span>
                    )}
                    <span className="line-clamp-2 min-w-0">
                      <HighlightedMatch
                        text={snippetAroundMatch(
                          stripPathPrefix(match.content),
                          regex,
                        )}
                        regex={regex}
                      />
                    </span>
                  </li>
                ))}
              </ul>
              {group.extraCount > 0 && (
                <p className="mt-1.5 text-xs italic text-zinc-400">
                  +{group.extraCount} more match{group.extraCount === 1 ? "" : "es"} in this paper
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Fallback for grep output we can't group by paper (single-paper grep, "No
 * matches…"): the raw `L<n>: text` lines, one per row, with the matched term
 * bolded the same way as the corpus view.
 */
export const GrepLinesView = ({
  output,
  pattern,
  ignoreCase,
  fixedStrings,
  wholeWords,
}: {
  output: string;
  pattern?: string;
  ignoreCase?: boolean;
  fixedStrings?: boolean;
  wholeWords?: boolean;
}) => {
  const regex = useMemo(
    () => compileGrepRegex(pattern, ignoreCase, fixedStrings, wholeWords),
    [fixedStrings, ignoreCase, pattern, wholeWords],
  );
  const lines = output.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    return <p className="py-12 text-center text-base italic text-zinc-500">(no output)</p>;
  }
  return (
    <ol className="divide-y divide-zinc-200/70">
      {lines.map((line, i) => {
        const m = /^(?:(\S*?):)?L(\d+):\s?(.*)$/.exec(line);
        if (!m) {
          return (
            <li key={i} className="py-2 font-mono text-xs leading-6 text-zinc-500">
              <HighlightedMatch text={line} regex={regex} />
            </li>
          );
        }
        return (
          <li key={i} className="flex items-baseline gap-3 py-2 text-sm leading-6 text-zinc-600">
            <span className="shrink-0 font-mono text-2xs text-zinc-400">L{m[2]}</span>
            <span className="min-w-0">
              <HighlightedMatch text={m[3]} regex={regex} />
            </span>
          </li>
        );
      })}
    </ol>
  );
};
